/* OperationalBlockersPanel.jsx — Operational intervention centre.
 *
 * Opens from the "View report" pill in the Operational Insight bar
 * on /today. NOT an analytics dashboard. This is a focused workflow
 * that turns the day's intelligence into one-tap intervention.
 *
 * Three calm sections (top → bottom):
 *   1. Slowest counter-parties  — bottlenecks grouped by party type
 *   2. Highest-risk transactions — ranked deals about to slip
 *   3. Stale transactions       — operational silence beyond threshold
 *
 * Every row carries an intervention CTA: "Chase all", "Send chase",
 * "Open deal". All API calls reuse the existing
 *   POST /api/deals/{id}/awaiting/chase
 * endpoint — zero new backend surface.
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, AlertTriangle, ArrowRight, Briefcase, Building2,
  Check, ChevronRight, Clock, Loader2, MoonStar, Search, Send,
  ShieldCheck, Sparkles, Users, ExternalLink,
} from "lucide-react";
import { SlideOver } from "../dealroom/DealRoomSheets";
import { api } from "../../lib/api";
import { invalidateDealActivity } from "../../hooks/useDealActivity";

/* ─── Helpers (mirrored from TodayPage so this stays self-contained) ── */
const _MS_DAY = 86_400_000;

const fmtGBP = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
};

const daysBetween = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.round((t - Date.now()) / _MS_DAY);
};

const relTime = (v) => {
  if (!v) return "";
  const t = typeof v === "number" ? v : Date.parse(v);
  if (Number.isNaN(t)) return "";
  const ms = Date.now() - t;
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < _MS_DAY) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / _MS_DAY)}d ago`;
};

const ownerLabel = (role) => {
  const r = String(role || "").toLowerCase();
  if (r.includes("solicitor_seller") || r === "seller_solicitor") return "Seller's solicitor";
  if (r.includes("solicitor_buyer")  || r === "buyer_solicitor")  return "Buyer's solicitor";
  if (r === "buyer")  return "Buyer";
  if (r === "seller") return "Seller";
  if (r.includes("broker") || r.includes("mortgage")) return "Mortgage broker";
  if (r.includes("management")) return "Management company";
  if (r.includes("surveyor")) return "Surveyor";
  if (r.includes("agent")) return "Agent";
  return r ? r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Responsible party";
};

const groupKeyFor = (role) => {
  const r = String(role || "").toLowerCase();
  if (r.includes("solicitor_seller") || r === "seller_solicitor") return "Seller solicitor";
  if (r.includes("solicitor_buyer")  || r === "buyer_solicitor")  return "Buyer solicitor";
  if (r.includes("broker") || r.includes("mortgage")) return "Buyer broker";
  if (r.includes("management")) return "Management company";
  if (r.includes("surveyor")) return "Surveyor";
  if (r === "buyer") return "Buyer";
  if (r === "seller") return "Seller";
  return ownerLabel(role);
};

const groupIconFor = (key) => {
  if (key.includes("solicitor")) return Users;
  if (key.includes("broker"))    return Briefcase;
  if (key.includes("Management"))return Building2;
  if (key.includes("Surveyor"))  return Search;
  return Users;
};

const fmtDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const nextActionFor = (item) => {
  const t = (item?.blocker?.title || "").toLowerCase();
  if (t.includes("contract") || t.includes("signed")) return "Chase seller solicitor for signed contract.";
  if (t.includes("enquir"))  return "Request response on outstanding enquiries.";
  if (t.includes("mortgage")) return "Chase broker for mortgage offer.";
  if (t.includes("search"))   return "Follow up with searches provider.";
  if (t.includes("survey"))   return "Chase surveyor for report delivery.";
  if (t.includes("signature")) return "Follow up for outstanding signature.";
  return `Follow up with ${ownerLabel(item?.blocker?.owner_role).toLowerCase()}.`;
};

const STALE_DAYS = 5;

/* ─── Single chase helper (reused everywhere) ──────────────────── */
async function sendChase(item) {
  await api.post(`/api/deals/${item.deal_id}/awaiting/chase`, {
    item_id: item.blocker?.id,
    title: item.blocker?.title,
    owner: item.blocker?.owner_role || null,
    days_waiting: item.blocker?.age_days || 0,
    send_email: false,
  });
  invalidateDealActivity(item.deal_id);
}

/* ════════════════════════════════════════════════════════════
   Panel
   ════════════════════════════════════════════════════════════ */
export function OperationalBlockersPanel({
  open, onClose,
  needsAttention = [],
  feeByDeal = {},
  tExByDeal = {},
  deals = [],
  onActed,
}) {
  /* Group by party type for Section 1 */
  const groups = useMemo(() => {
    const map = new Map();
    for (const n of needsAttention) {
      const key = groupKeyFor(n.blocker?.owner_role);
      const entry = map.get(key) || {
        key,
        role: n.blocker?.owner_role,
        items: [],
        deals: new Set(),
        ageSum: 0,
        ageCount: 0,
        oldest: 0,
        feeSum: 0,
      };
      entry.items.push(n);
      entry.deals.add(n.deal_id);
      entry.ageSum += Number(n.blocker?.age_days || 0);
      entry.ageCount += 1;
      entry.oldest = Math.max(entry.oldest, Number(n.blocker?.age_days || 0));
      entry.feeSum += Number(feeByDeal[n.deal_id] || 0);
      map.set(key, entry);
    }
    return [...map.values()]
      .map((g) => ({
        ...g,
        dealCount: g.deals.size,
        avg: g.ageCount ? g.ageSum / g.ageCount : 0,
      }))
      .sort((a, b) => {
        // Rank by deal count, then avg age, then fee exposure.
        if (b.dealCount !== a.dealCount) return b.dealCount - a.dealCount;
        if (b.avg !== a.avg) return b.avg - a.avg;
        return b.feeSum - a.feeSum;
      });
  }, [needsAttention, feeByDeal]);

  /* Ranked highest-risk transactions: one row per affected deal */
  const highRisk = useMemo(() => {
    const byDeal = new Map();
    for (const n of needsAttention) {
      if (!byDeal.has(n.deal_id)) byDeal.set(n.deal_id, n);
    }
    const sevWeight = { blocking: 0, warning: 1, info: 2 };
    return [...byDeal.values()]
      .map((n) => {
        const d2e = daysBetween(tExByDeal[n.deal_id]);
        return {
          ...n,
          d2e,
          fee: Number(feeByDeal[n.deal_id] || 0),
          target: tExByDeal[n.deal_id],
        };
      })
      .sort((a, b) => {
        // 1) blocking severity first
        const sa = sevWeight[a.blocker?.severity] ?? 9;
        const sb = sevWeight[b.blocker?.severity] ?? 9;
        if (sa !== sb) return sa - sb;
        // 2) target exchange soonest first (negative = overdue, treat as urgent)
        const da = a.d2e === null ? 99999 : a.d2e;
        const db = b.d2e === null ? 99999 : b.d2e;
        if (da !== db) return da - db;
        // 3) larger fee exposure first
        return b.fee - a.fee;
      })
      .slice(0, 8);
  }, [needsAttention, feeByDeal, tExByDeal]);

  /* Stale transactions — operational silence ≥ STALE_DAYS on the
   * most-overdue blocker. Mirrors how an agent thinks: "where have
   * I had no reply for nearly a week?". One row per deal. */
  const stale = useMemo(() => {
    const byDeal = new Map();
    for (const n of needsAttention) {
      const age = Number(n.blocker?.age_days || 0);
      if (age < STALE_DAYS) continue;
      const existing = byDeal.get(n.deal_id);
      if (!existing || age > Number(existing.blocker?.age_days || 0)) {
        byDeal.set(n.deal_id, n);
      }
    }
    // Stage label from the deals array
    const stageByDeal = new Map();
    for (const d of Array.isArray(deals) ? deals : []) {
      if (d?.id) stageByDeal.set(d.id, d.stage || "");
    }
    return [...byDeal.values()]
      .map((n) => ({
        ...n,
        stage: stageByDeal.get(n.deal_id) || "",
      }))
      .sort((a, b) => Number(b.blocker?.age_days || 0) - Number(a.blocker?.age_days || 0))
      .slice(0, 6);
  }, [needsAttention, deals]);

  const isEmpty = !needsAttention.length;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Operational blockers"
      subtitle="Parties currently slowing transaction progression."
      wide
      testid="op-blockers-panel"
    >
      {isEmpty ? <EmptyState /> : (
        <div className="space-y-7">
          <SlowestCounterParties groups={groups} onActed={onActed} />
          <HighRiskTransactions items={highRisk} onActed={onActed} />
          <StaleTransactions items={stale} />
        </div>
      )}
    </SlideOver>
  );
}

/* ════════════════════════════════════════════════════════════
   Section 1 — Slowest counter-parties
   ════════════════════════════════════════════════════════════ */
function SlowestCounterParties({ groups, onActed }) {
  if (!groups.length) return null;
  return (
    <section data-testid="op-blockers-section-counterparties">
      <SectionTitle
        kicker="Section 01"
        title="Slowest counter-parties"
        subtitle="Bottlenecks ranked by affected deals, response delay and exposure."
      />
      <div className="space-y-2.5 mt-3">
        {groups.map((g, i) => (
          <CounterPartyCard key={g.key} group={g} index={i} onActed={onActed} />
        ))}
      </div>
    </section>
  );
}

function CounterPartyCard({ group, index, onActed }) {
  const Icon = groupIconFor(group.key);
  const high = group.avg >= 4;
  const [chasing, setChasing] = useState(false);
  const [chasedCount, setChasedCount] = useState(0);

  const chaseAll = useCallback(async () => {
    if (chasing) return;
    setChasing(true);
    let ok = 0;
    let throttled = 0;
    let failed = 0;
    for (const item of group.items) {
      try {
        await sendChase(item);
        ok += 1;
      } catch (err) {
        if (err?.response?.status === 429) throttled += 1;
        else failed += 1;
      }
      // Tiny stagger so we never hammer the rate-limiter.
      await new Promise((r) => setTimeout(r, 180));
    }
    setChasedCount(ok);
    setChasing(false);
    if (ok > 0) {
      toast.success(`${ok} chase${ok === 1 ? "" : "s"} logged · ${group.key}`, {
        description: throttled || failed
          ? `${throttled} throttled · ${failed} failed`
          : "Timelines updated across affected deals.",
      });
      onActed?.();
    } else if (throttled > 0) {
      toast(`Just chased — give it a moment.`);
    } else {
      toast.error("Could not chase any of these deals");
    }
  }, [group, chasing, onActed]);

  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 overflow-hidden"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid={`op-blockers-cp-card-${index}`}
    >
      <div className="px-4 py-3.5 flex items-start gap-3">
        <span
          className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
          style={{ background: "rgba(245,158,11,0.10)", color: "#B45309" }}
        >
          <Icon size={15} strokeWidth={2.2} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold text-slate-900 truncate">{group.key}</p>
              <p className="text-[11.5px] text-slate-500 mt-0.5">
                {group.dealCount} deal{group.dealCount === 1 ? "" : "s"} blocked
              </p>
            </div>
            <span
              className="text-[10.5px] font-bold tabular-nums px-2 h-[20px] rounded inline-flex items-center shrink-0"
              style={{
                background: high ? "rgba(239,68,68,0.10)" : "rgba(245,158,11,0.12)",
                color: high ? "#B91C1C" : "#B45309",
              }}
            >
              {group.avg.toFixed(1)}d avg
            </span>
          </div>

          <div className="mt-2.5 grid grid-cols-3 gap-2 text-[11px]">
            <Metric label="Affected deals" value={group.dealCount} />
            <Metric label="Fee exposure"   value={fmtGBP(group.feeSum)} />
            <Metric label="Oldest delay"   value={`${group.oldest}d`} />
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="h-[30px] px-3 rounded-lg text-[12px] font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 inline-flex items-center gap-1.5 transition-colors"
              data-testid={`op-blockers-cp-view-${index}`}
            >
              {expanded ? "Hide deals" : `View deals (${group.dealCount})`}
              <ChevronRight size={11} className={expanded ? "rotate-90 transition-transform" : "transition-transform"} />
            </button>
            {chasedCount > 0 ? (
              <span
                className="h-[30px] px-3 rounded-lg inline-flex items-center gap-1.5 text-[12px] font-semibold"
                style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}
                data-testid={`op-blockers-cp-chased-${index}`}
              >
                <Check size={11} strokeWidth={2.6} />
                Chased {chasedCount}
              </span>
            ) : (
              <button
                type="button"
                onClick={chaseAll}
                disabled={chasing}
                className="h-[30px] px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                style={{
                  color: "#5B21B6",
                  background: "#F5F3FF",
                  border: "1px solid rgba(139,92,246,0.28)",
                }}
                data-testid={`op-blockers-cp-chase-all-${index}`}
              >
                {chasing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} strokeWidth={2.4} />}
                {chasing ? "Chasing…" : `Chase all (${group.dealCount})`}
              </button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/40">
          <ul className="divide-y divide-slate-100">
            {group.items.map((item, idx) => (
              <li key={`${item.deal_id}-${item.blocker?.id}-${idx}`}>
                <Link
                  to={`/deal-rooms/${item.deal_id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-white transition-colors"
                  data-testid={`op-blockers-cp-deal-${index}-${idx}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-semibold text-slate-900 truncate">
                      {item.property_address}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {item.blocker?.title}
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-500 tabular-nums shrink-0">
                    {item.blocker?.age_days || 0}d
                  </span>
                  <ChevronRight size={12} className="text-slate-300 shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-2.5 py-1.5">
      <p className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-slate-400">{label}</p>
      <p className="text-[12.5px] font-semibold text-slate-900 tabular-nums truncate">{value}</p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Section 2 — Highest-risk transactions
   ════════════════════════════════════════════════════════════ */
function HighRiskTransactions({ items, onActed }) {
  if (!items.length) return null;
  return (
    <section data-testid="op-blockers-section-risk">
      <SectionTitle
        kicker="Section 02"
        title="Highest-risk transactions"
        subtitle="Deals likely to slip, with overdue milestones or stalled exchanges."
      />
      <div className="space-y-2 mt-3">
        {items.map((item, i) => (
          <RiskRow key={`${item.deal_id}-${item.blocker?.id}-${i}`} item={item} index={i} onActed={onActed} />
        ))}
      </div>
    </section>
  );
}

function RiskRow({ item, index, onActed }) {
  const nav = useNavigate();
  const sev = item.blocker?.severity || "info";
  const rail = sev === "blocking" ? "#EF4444" : sev === "warning" ? "#F97316" : "#F59E0B";
  const fg   = sev === "blocking" ? "#B91C1C" : sev === "warning" ? "#C2410C" : "#B45309";
  const [sending, setSending] = useState(false);
  const [chased, setChased] = useState(false);

  const onChase = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (sending) return;
    setSending(true);
    try {
      await sendChase(item);
      setChased(true);
      toast.success(`Chase logged · ${item.deal_name || item.property_address}`);
      onActed?.();
    } catch (err) {
      const s = err?.response?.status;
      if (s === 429) toast("Just chased — give it a moment.");
      else toast.error("Could not send chase");
    } finally {
      setSending(false);
    }
  }, [item, sending, onActed]);

  const teLabel = item.target
    ? `${fmtDateShort(item.target)}${item.d2e !== null ? ` · ${item.d2e < 0 ? `${Math.abs(item.d2e)}d overdue` : item.d2e === 0 ? "today" : `in ${item.d2e}d`}` : ""}`
    : "—";

  return (
    <div
      className="relative rounded-2xl bg-white border border-slate-100 overflow-hidden"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid={`op-blockers-risk-row-${index}`}
    >
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1" style={{ background: rail }} />
      <div className="pl-4 pr-3 py-3.5 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-start">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-slate-900 truncate">{item.property_address}</p>
          <p className="text-[12px] text-slate-700 mt-0.5 leading-snug truncate">{item.blocker?.title}</p>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <MiniMetric
              label="Target exchange"
              value={teLabel}
              tone={item.d2e !== null && item.d2e <= 7 ? fg : "#0F172A"}
            />
            <MiniMetric
              label="Fee at risk"
              value={fmtGBP(item.fee || null)}
              tone={fg}
            />
          </div>

          <p className="text-[11.5px] text-slate-500 mt-2 leading-snug">
            <Sparkles size={10} className="inline align-[-2px] text-violet-500 mr-1" />
            {nextActionFor(item)}
          </p>
        </div>

        <div className="flex sm:flex-col items-stretch gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => nav(`/deal-rooms/${item.deal_id}`)}
            className="h-[30px] px-3 rounded-lg text-[12px] font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 inline-flex items-center gap-1.5 transition-colors"
            data-testid={`op-blockers-risk-open-${index}`}
          >
            <ExternalLink size={11} />
            Open deal
          </button>
          {chased ? (
            <span
              className="h-[30px] px-3 rounded-lg inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold"
              style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}
            >
              <Check size={11} strokeWidth={2.6} />
              Chased
            </span>
          ) : (
            <button
              type="button"
              onClick={onChase}
              disabled={sending}
              className="h-[30px] px-3 rounded-lg text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
              style={{
                color: "#5B21B6",
                background: "#F5F3FF",
                border: "1px solid rgba(139,92,246,0.28)",
              }}
              data-testid={`op-blockers-risk-chase-${index}`}
            >
              {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} strokeWidth={2.4} />}
              Send chase
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniMetric({ label, value, tone }) {
  return (
    <div className="rounded-lg bg-slate-50/70 border border-slate-100 px-2.5 py-1.5">
      <p className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-slate-400">{label}</p>
      <p className="text-[12.5px] font-semibold tabular-nums truncate" style={{ color: tone || "#0F172A" }}>{value}</p>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Section 3 — Stale transactions
   ════════════════════════════════════════════════════════════ */
function StaleTransactions({ items }) {
  return (
    <section data-testid="op-blockers-section-stale">
      <SectionTitle
        kicker="Section 03"
        title="Stale transactions"
        subtitle={`No party response for ${STALE_DAYS}+ days · progression at a standstill.`}
      />
      {items.length === 0 ? (
        <div
          className="mt-3 rounded-2xl bg-white border border-slate-100 px-4 py-4 flex items-start gap-3"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
        >
          <span
            className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
            style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}
          >
            <ShieldCheck size={14} strokeWidth={2.2} />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900">Nothing has gone stale</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">No transaction has been silent for {STALE_DAYS}+ days.</p>
          </div>
        </div>
      ) : (
        <ul
          className="mt-3 rounded-2xl bg-white border border-slate-100 divide-y divide-slate-100 overflow-hidden"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
        >
          {items.map((item, i) => (
            <li key={`${item.deal_id}-stale-${i}`}>
              <Link
                to={`/deal-rooms/${item.deal_id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/60 transition-colors"
                data-testid={`op-blockers-stale-row-${i}`}
              >
                <span
                  className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
                  style={{ background: "rgba(148,163,184,0.14)", color: "#475569" }}
                >
                  <MoonStar size={14} strokeWidth={2.2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-slate-900 truncate">{item.property_address}</p>
                  <p className="text-[11.5px] text-slate-500 truncate">
                    {ownerLabel(item.blocker?.owner_role)}
                    {item.stage && <>
                      <span className="text-slate-300 mx-1.5">·</span>
                      <span>{stageLabel(item.stage)}</span>
                    </>}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[12px] font-bold tabular-nums text-slate-900">
                    {item.blocker?.age_days || 0}d inactive
                  </p>
                  <p className="text-[10.5px] text-slate-400 mt-0.5">
                    {item.blocker?.last_chased_at ? `Last chase ${relTime(item.blocker.last_chased_at)}` : "No chase yet"}
                  </p>
                </div>
                <ChevronRight size={12} className="text-slate-300 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function stageLabel(stage = "") {
  const s = String(stage).toLowerCase();
  if (!s) return "—";
  if (s.includes("enquir"))   return "Enquiries";
  if (s.includes("memo"))     return "Memorandum";
  if (s.includes("mortgage")) return "Mortgage offer";
  if (s.includes("search"))   return "Searches";
  if (s === "exchange_ready" || s === "exchange-ready") return "Exchange ready";
  if (s.includes("exchang"))  return "Exchanged";
  if (s.includes("complet"))  return "Completion";
  if (s === "offer")          return "Offer";
  return stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, " ");
}

/* ════════════════════════════════════════════════════════════
   Shell helpers
   ════════════════════════════════════════════════════════════ */
function SectionTitle({ kicker, title, subtitle }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-violet-700">{kicker}</p>
      <h3 className="text-[15px] font-bold tracking-[-0.01em] text-slate-900 mt-1">{title}</h3>
      {subtitle && <p className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{subtitle}</p>}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100 p-5 flex items-start gap-3"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid="op-blockers-empty"
    >
      <span
        className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
        style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}
      >
        <ShieldCheck size={15} strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-slate-900">No operational blockers right now</p>
        <p className="text-[11.5px] text-slate-500 mt-0.5 leading-relaxed">
          Every active transaction is progressing without intervention required.
          ChainFlow will resurface this panel the moment a party starts to drift.
        </p>
      </div>
    </div>
  );
}

export default OperationalBlockersPanel;
